import { authFetch } from '@services/authFetch';
import { AuthEffectHandler } from '@classes/AuthEffectHandler';
import { RefreshNetworkError, RefreshUnauthorizedError } from '@classes/authErrors';
import { logError } from '@utils/usefulFuncs';

export const ACCESS_TOKEN_STORAGE_KEY = 'access_token';
export const SIGNED_OUT_AT_STORAGE_KEY = 'signed_out_at';
const REFRESH_PATH = '/api/v1/auth/refresh';
const REFRESH_LOCK_NAME = 'tornado-auth-refresh';

class _AuthTokenManager {
  constructor() {
    this._refreshPromise = null;
    this._storageListener = null;
  }

  init() {
    try {
      window.localStorage.removeItem('X-CSRF-Token');
    } catch (e) {
      logError('AuthTokenManager init csrf scrub', e);
    }
    if (this._storageListener) return;
    this._storageListener = (e) => {
      if (e.key === ACCESS_TOKEN_STORAGE_KEY && e.newValue === null) {
        AuthEffectHandler.onLossOfAuth();
      }
    };
    window.addEventListener('storage', this._storageListener);
  }

  getAccessToken() {
    try {
      return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  setAccessToken(token) {
    if (!token) return;
    window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
    window.localStorage.removeItem(SIGNED_OUT_AT_STORAGE_KEY);
  }

  clear() {
    window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  }

  markSignedOut() {
    window.localStorage.setItem(SIGNED_OUT_AT_STORAGE_KEY, String(Date.now()));
  }

  isSignedOutIntentSet() {
    return !!window.localStorage.getItem(SIGNED_OUT_AT_STORAGE_KEY);
  }

  async refresh(staleToken) {
    // Don't resurrect a session the user explicitly tore down — even if the
    // HttpOnly refresh cookie wasn't evicted (offline sign-out, slow server),
    // the local intent overrides the cookie.
    if (!this.getAccessToken() && this.isSignedOutIntentSet()) {
      throw new RefreshUnauthorizedError();
    }

    const doRefreshGuarded = async () => {
      // Compare-and-refresh: if storage moved past the stale token (another
      // tab or earlier in-flight refresh already rotated), return current.
      const current = this.getAccessToken();
      if (current && current !== staleToken) {
        return current;
      }
      if (this._refreshPromise) {
        return this._refreshPromise;
      }
      this._refreshPromise = this._doRefresh();
      try {
        return await this._refreshPromise;
      } finally {
        this._refreshPromise = null;
      }
    };

    const useLocks = typeof navigator !== 'undefined' && navigator.locks?.request;
    return useLocks ? navigator.locks.request(REFRESH_LOCK_NAME, doRefreshGuarded) : doRefreshGuarded();
  }

  async _doRefresh() {
    let response;
    try {
      response = await authFetch(REFRESH_PATH, 'POST', null);
    } catch (e) {
      // Network error — leave token in place so caller can surface transient
      // failure rather than blowing up the session.
      throw new RefreshNetworkError(e);
    }
    if (response.status === 401 || response.status === 403) {
      this.clear();
      try {
        AuthEffectHandler.onLossOfAuth();
      } catch (e) {
        logError('AuthTokenManager onLossOfAuth', e);
      }
      throw new RefreshUnauthorizedError();
    }
    if (response.status !== 200) {
      throw new RefreshNetworkError(new Error(`refresh status ${response.status}`));
    }
    const data = await response.json();
    const token = data?.access_token;
    if (!token) {
      throw new RefreshNetworkError(new Error('refresh response missing access_token'));
    }
    this.setAccessToken(token);
    return token;
  }
}

export const AuthTokenManager = new _AuthTokenManager();
export { RefreshNetworkError, RefreshUnauthorizedError };
